import PropTypes from 'prop-types';
import React from 'react';
import Button from './Button';
import Error from './Error';
import { SelectInput, TextInput } from './Form';
import { formatPhoneNumber, getCanadaProvinces, getCountryList, getUnitedStates } from './utilities';
import { COUNTRY_ID } from './country.constant';
import { validateAddress } from './address.util';

/**
 * Enum-like constant for address selection options.
 * Used to represent special selection values from a dropdown.
 *
 * @readonly
 * @enum {string}
 */
export const AddressSelectionOption = {
  NEW: 'new',
  RESET: 'reset',
};


// 'blankAddress' keys match what is returned from they 'UserType'=>'AddressType' GraphQL query
const blankAddress = {
  AddressID: null,
  First: '',
  Last: '',
  CompanyName: '',
  AddressLineOne: '',
  AddressLineTwo: '',
  City: '',
  State: '',
  Zip: '',
  CountryID: '',
  Phone: '',
};

/**
 * @class AddressForm
 * @extends React.Component
 *
 * A controlled React form component for collecting and validating address information.
 * Supports selecting from saved addresses or entering a new one, with validation for
 * names, country, state, zip/postal code, phone number, and optional artist connection note.
 *
 * Props:
 * @prop {Array<Object>} addresses - List of existing address objects available to select from.
 * @prop {Object} defaultAddress - The default address to prefill in the form.
 * @prop {boolean} showCreateAddress - Whether to initially show the address creation form.
 * @prop {boolean} showAddressSelector - Whether to show the address dropdown selector.
 * @prop {boolean} showConnectionToArtist - Whether to include the "Connection To Artist" field.
 * @prop {boolean} showNote - Whether to include an optional note field.
 * @prop {boolean} loading - Whether to show a loading indicator on submit.
 * @prop {boolean} error - Whether an error occurred and should be displayed.
 * @prop {Function} onSubmit - Callback when the form is successfully submitted. Called with the address object.
 * @prop {Function} onSelect - Callback when an existing address is selected.
 * @prop {boolean} refreshDefaultAddress - Whether to re-initialize the form when defaultAddress changes.
 * @prop {string} buttonText - The text to display on the submit button.
 *
 * State:
 * @state {Object} address - The address form data object.
 * @state {Object} formErrors - A mapping of field names to validation error messages.
 * @state {boolean} showCreateAddress - Whether the address creation form is shown.
 * @state {boolean} dirty - Whether any form input has changed.
 *
 * Validation:
 * - Validates required fields like name, address, city, state, zip, phone
 * - Enforces format for U.S. and Canadian zip/postal codes
 * - Prevents PO Box addresses for shipping
 *
 * Example usage:
 * <AddressForm
 *   addresses={[...]}
 *   defaultAddress={{ First: 'John', Last: 'Doe' }}
 *   showCreateAddress={true}
 *   showAddressSelector={true}
 *   showConnectionToArtist={false}
 *   onSubmit={(address) => console.log(address)}
 * />
 */
export class AddressForm extends React.Component {
  state = {
    formErrors: {},
    showCreateAddress: this.props.showCreateAddress,
    address: this.props.defaultAddress || blankAddress,
  };

  /**
   * Lifecycle method that initializes the address state when the component mounts.
   * It merges the default address from props with a blank template and ensures the CountryID
   * is normalized (set to an empty string if null or undefined).
   *
   * This replaces the deprecated `componentWillMount` and ensures consistent initialization
   * of form data before the first render.
   *
   * @memberof AddressForm
   */
  componentDidMount() {
    const { defaultAddress } = this.props;
    const address = {
      ...blankAddress,
      ...(defaultAddress || {}),
      CountryID: defaultAddress?.CountryID ?? '',
    };

    this.setState({ address });
  }

  /**
   * Lifecycle method that updates the address state if the `defaultAddress` prop changes
   * and `refreshDefaultAddress` is true. Ensures the form reflects updated address data.
   *
   * @param {Object} prevProps - The previous props before the update.
   * @param {Object} prevProps.defaultAddress - The default address prop before update.
   */
  componentDidUpdate(prevProps) {
    const { defaultAddress, refreshDefaultAddress } = this.props;
    if (defaultAddress !== prevProps.defaultAddress && refreshDefaultAddress) {
      this.setState({ address: defaultAddress });
    }
  }

  /**
   * Handles changes in the address selector dropdown.
   *
   * - If "new" is selected, resets the form to a blank address and enables the address creation form.
   * - If an existing address is selected, sets it as the current form address and invokes the `onSelect` callback.
   *
   * @function
   * @param {React.ChangeEvent<HTMLSelectElement>} e - The change event triggered by selecting an address.
   *
   * @example
   * <select onChange={this.handleSelectAddress}>
   *   <option value="new">-- Use a new address --</option>
   *   <option value="123">123 Main St, Springfield</option>
   * </select>
   */
  handleSelectAddress = (e) => {
    // selectedId can be 'new', 'reset', or an AddressID. 'new' and 'reset' values come from the select input
    const selectedId = e.target.value;

    if (selectedId === AddressSelectionOption.NEW) {
      this.setState({
        address: { ...blankAddress, AddressID: AddressSelectionOption.NEW },
        showCreateAddress: true,
      });
      return;
    }

    const address = this.props.addresses?.find((a) => Number(a.AddressID) === Number(selectedId));

    if (address) {
      this.setState({ address }, () => {
        this.props.onSelect?.(address);
      });
    }
  };

  /**
 * Handles input changes for the phone number field.
 * Formats the entered phone number and updates the `address.Phone` value in the component state.
 *
 * @function
 * @param {React.ChangeEvent<HTMLInputElement>} event - The change event from the phone number input field.
 *
 * @example
 * <input type="text" onChange={handlePhoneInput} />
 */
  handlePhoneInput = (event) => {
    const { value } = event.target;
    const formattedPhone = formatPhoneNumber(value);

    this.setState((prevState) => ({
      address: {
        ...prevState.address,
        Phone: formattedPhone,
      },
      dirty: true,
    }));
  };


  /**
   * Returns a change handler function for updating a specific address field in state.
   * If the changed field is `CountryID`, it also resets the `State` field.
   *
   * @function
   * @param {string} name - The name of the address field to update.
   * @return {Function} A change event handler.
   */
  handleInput = (name) => (event) => {
    const value = event.target.value;

    this.setState((prevState) => {
      const updatedAddress = {
        ...prevState.address,
        [name]: value,
        ...(name === 'CountryID' && { State: '' }),
      };

      return {
        address: updatedAddress,
        dirty: true,
      };
    });
  };


  /**
   * Handles the form submission for the address form.
   *
   * - Prevents default form behavior.
   * - Validates the address fields using `validateAddress`.
   * - Displays errors if validation fails.
   * - Normalizes the `AddressID` to `null` if it is marked as 'new' or 'reset'.
   * - Calls the `onSubmit` prop with the normalized address.
   * - Updates local state to reflect submission success.
   *
   * @function
   * @param {React.FormEvent<HTMLFormElement>} e - The form submit event.
   */
  handleSubmit = (e) => {
    e.preventDefault();
    const { address } = this.state;
    const formErrors = validateAddress(address);

    if (Object.keys(formErrors).length > 0) {
      this.setState({ formErrors });
      return;
    }

    // Normalize address ID if it's marked as "new" or "reset"
    const normalizedAddress = [AddressSelectionOption.RESET, AddressSelectionOption.NEW].includes(address.AddressID)
      ? { ...address, AddressID: null }
      : address;

    if (this.props.onSubmit) {
      this.props.onSubmit(normalizedAddress);
    }

    this.setState({
      addressToSubmit: normalizedAddress,
      dirty: false,
      formErrors: {},
      showCreateAddress: false,
    });
  };

  render() {
    const {
      error,
      loading,
      addresses,
      showAddressSelector,
    } = this.props;

    const { address, formErrors, showCreateAddress } = this.state;

    const countryList = getCountryList();

    const countryOptionList = [
      { value: '', label: '-- Select Country (required) --' },
      ...countryList.map((c) => ({
        value: c.id,
        label: c.name,
      })),
    ];

    const countryID = address?.CountryID ? Number(address.CountryID) : COUNTRY_ID.UNITED_STATES;
    const statesList = countryID === COUNTRY_ID.CANADA ? getCanadaProvinces() : getUnitedStates();

    return (
      <div id="address-form">
        {showAddressSelector && (
          <div style={{ marginBottom: 15 }}>
            <SelectInput
              id="select-existing-address-input"
              name="address"
              label="Saved Addresses"
              value={
                address.AddressID
                  ? address.AddressID
                  : addresses.length
                    ? 'reset'
                    : 'new'
              }
              onChange={this.handleSelectAddress}
              style={{ width: '100%' }}
              optionList={[
                {
                  value: 'reset',
                  disabled: !addresses.length,
                  label: '-- Saved Addresses --',
                },
              ].concat(
                addresses.map((x) => ({
                  value: x.AddressID,
                  label: `${x.AddressLineOne}, ${x.City} ${x.State}, ${x.Zip}`,
                })).concat([{ value: 'new', label: '-- Use a new address --' }]),
              )}
            />
          </div>
        )}
        {(showCreateAddress || this.props.showCreateAddress) && (
          <div>
            <TextInput
              id="first-name-input"
              name="First"
              label="First Name"
              error={formErrors.First}
              fullWidth
              value={address.First || ''}
              onChange={this.handleInput('First')}
              required
            />
            <TextInput
              id="last-name-input"
              name="Last"
              label="Last Name"
              error={formErrors.Last}
              fullWidth
              value={address.Last || ''}
              onChange={this.handleInput('Last')}
              required
            />
            <TextInput
              id="company-name-input"
              name="CompanyName"
              label="Company"
              error={formErrors.CompanyName}
              fullWidth
              value={address.CompanyName || ''}
              onChange={this.handleInput('CompanyName')}
            />
            <SelectInput
              id="country-select"
              name="CountryID"
              label="Country"
              value={address.CountryID}
              error={formErrors.CountryID}
              onChange={this.handleInput('CountryID')}
              optionList={countryOptionList}
              required
            />
            <TextInput
              id="address-line-one-input"
              name="AddressLineOne"
              label="Street Address"
              error={formErrors.AddressLineOne}
              fullWidth
              value={address.AddressLineOne || ''}
              onChange={this.handleInput('AddressLineOne')}
              required
            />
            <TextInput
              id="address-line-two-input"
              name="AddressLineTwo"
              label="Apt / Suite / Other"
              error={formErrors.AddressLineTwo}
              fullWidth
              value={address.AddressLineTwo || ''}
              onChange={this.handleInput('AddressLineTwo')}
            />
            <TextInput
              id="city-input"
              name="City"
              label="City"
              error={formErrors.City}
              fullWidth
              value={address.City || ''}
              onChange={this.handleInput('City')}
              required
            />
            {countryID === COUNTRY_ID.UNITED_STATES || countryID === COUNTRY_ID.CANADA ? (
              <SelectInput
                id="state-select"
                name="State"
                label="State"
                value={address.State}
                onChange={this.handleInput('State')}
                error={formErrors.State}
                optionList={[
                  {
                    value: '',
                    label: '-- Select State (required) --',
                  },
                ].concat(
                  statesList.map((c) => ({
                    value: c.abbreviation,
                    label: c.name,
                  })),
                )}
                required
              />
            ) : (
              <TextInput
                name="State"
                label="State"
                error={formErrors.State}
                fullWidth
                value={address.State || ''}
                onChange={this.handleInput('State')}
                required
              />
            )}
            <TextInput
              id="zip-input"
              name="Zip"
              label="Zip/Postal Code"
              error={formErrors.Zip}
              fullWidth
              value={address.Zip || ''}
              onChange={this.handleInput('Zip')}
              required
            />
            <TextInput
              id="phone-input"
              name="Phone"
              label="Phone"
              error={formErrors.Phone}
              fullWidth
              value={address.Phone || ''}
              onChange={this.handlePhoneInput}
              required
            />
          </div>
        )}
        {error && <Error>error occurred</Error>}

        <Button
          id="save-address-btn"
          name="btn-save-address"
          fullWidth
          onClick={this.handleSubmit}
          loading={loading}
          style={{ marginTop: '1rem' }}
        >
          {this.props.buttonText}
        </Button>
      </div>
    );
  }
}

AddressForm.defaultProps = {
  showCreateAddress: true,
  showAddressSelector: true,
  defaultAddress: blankAddress,
  buttonText: 'Save',
  addresses: [],
  loading: false,
  error: null,
  refreshDefaultAddress: true,
  onCancel: () => false,
  onSubmit: () => false,
  onSelect: () => false,
};

AddressForm.propTypes = {
  showAddressSelector: PropTypes.bool,
  defaultAddress: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  buttonText: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  addresses: PropTypes.array,
  refreshDefaultAddress: PropTypes.bool,
  onSubmit: PropTypes.func,
  onSelect: PropTypes.func,
  onCancel: PropTypes.func,
};

export default AddressForm;
